<script setup>
import { ref, watch } from "vue";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  imageSource: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "cancel", "save"]);

const cropperRef = ref(null);
const cropperResult = ref(null);

const stencilProps = {
  aspectRatio: 1,
  movable: true,
  resizable: true,
  handlers: {
    eastNorth: true,
    north: true,
    westNorth: true,
    west: true,
    westSouth: true,
    south: true,
    eastSouth: true,
    east: true,
  },
  handlersClasses: {
    default: "handler-default",
  },
  lineWidth: 2,
};

function onCropperChange({ canvas }) {
  cropperResult.value = canvas;
}

function saveCroppedImage() {
  if (!cropperResult.value) return;

  const maxSize = 400;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const ratio = Math.min(
    maxSize / cropperResult.value.width,
    maxSize / cropperResult.value.height
  );

  canvas.width = cropperResult.value.width * ratio;
  canvas.height = cropperResult.value.height * ratio;

  ctx.drawImage(cropperResult.value, 0, 0, canvas.width, canvas.height);

  const base64Image = canvas.toDataURL("image/jpeg", 0.85);

  emit("save", {
    base64: base64Image,
    name: "cropped-image.jpg",
  });
  emit("update:modelValue", false);
}

function cancelCropping() {
  cropperResult.value = null;
  emit("cancel");
  emit("update:modelValue", false);
}

watch(
  () => props.modelValue,
  (visible) => {
    if (!visible) cropperResult.value = null;
  }
);
</script>

<template>
  <div v-if="modelValue" class="cropper-modal" data-modal>
    <div class="cropper-container">
      <div class="modal__header">
        <h2 class="modal__title">Crop Your Preview Image</h2>
        <button class="modal__close" @click="cancelCropping" type="button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="modal__body">
        <Cropper
          ref="cropperRef"
          :src="imageSource"
          @change="onCropperChange"
          :stencil-props="stencilProps"
          class="cropper"
        />
      </div>
      <div class="modal__footer">
        <button
          class="btn btn--secondary"
          @click="cancelCropping"
          type="button"
          data-testid="cancel-cropped-image-button"
        >
          Cancel
        </button>
        <button
          class="btn btn--primary"
          @click="saveCroppedImage"
          type="button"
          data-testid="save-cropped-image-button"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass">
.cropper-modal {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
  isolation: isolate;
}

.cropper-container {
  @apply overflow-hidden bg-echoes-background-black rounded-[13px] shadow-xl w-full max-h-[85vh] mt-10 flex flex-col;
  animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  z-index: 1;
  max-width: 540px;
}

.modal__header {
  @apply sticky top-0 z-10 flex items-center justify-between p-4 border-b border-echoes-border-gray bg-echoes-background-black;
}

.modal__title {
  @apply text-xl font-semibold text-white/80;
}

.modal__close {
  @apply text-white/80 hover:text-white p-1 rounded-full transition-colors;
}

.modal__body {
  @apply p-4 overflow-y-auto;
  flex: 1;
}

.modal__footer {
  @apply sticky bottom-0 z-10 p-4 border-t border-echoes-border-gray flex items-center justify-end gap-3 bg-echoes-background-black;
}

.btn {
  @apply px-4 py-2 rounded-lg font-medium transition-colors;
}

.btn--secondary {
  @apply px-4 py-1.5 rounded-lg text-sm font-medium transition-colors hover:border-echoes-primary hover:text-echoes-primary bg-echoes-background-black text-white/80 border border-echoes-border-gray;
}

.btn--primary {
  @apply px-4 py-1.5 rounded-lg text-sm font-medium transition-colors bg-echoes-primary border-none hover:bg-echoes-primary/50;
}

::deep(.cropper) {
  @apply bg-zinc-900;
}

::deep(.handler-default) {
  @apply w-3 h-3 bg-echoes-primary rounded-full;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

